// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import {
    canCheckoutWithCart,
    getCurrentCartState,
    isCartEmpty,
} from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import IconCompare from "@plumberssupply/XDL/Icons/IconCompare";
import { iconLinkCommonStyles, iconStyles, mergeWithCss, parseBoolean } from "@plumberssupply/XDL/Utilities";
import React, { FC } from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState) => {
    const cartState = getCurrentCartState(state);
    const cart = cartState.value;
    const isCartLoading = cartState.isLoading;

    return {
        canCheckoutWithCart: canCheckoutWithCart(cart),
        isLoading: isCartLoading,
        isCartEmpty: isCartEmpty(cart),
        session: getSession(state),
        warehouseTransferUrl: getPageLinkByPageType(state, "WarehouseTransfer")?.url || "/WarehouseTransfer",
    };
};

type Props = WidgetProps & ReturnType<typeof mapStateToProps> & HasHistory;

export interface TransferItemsStyles {
    wrapper: InjectableCss;
    link: LinkPresentationProps;
}

export const transferItemsStyles: TransferItemsStyles = {
    wrapper: {
        css: css`
            margin-top: 16px;
        `,
    },
    link: {
        ...mergeWithCss(iconLinkCommonStyles(IconCompare), { ...iconStyles }),
    },
};

const styles = transferItemsStyles;

const TransferItems: FC<Props> = ({
    history,
    isCartEmpty,
    canCheckoutWithCart,
    isLoading,
    session,
    warehouseTransferUrl,
}) => {
    const { displayWarehouseTransfer } = session.properties;

    const goToHandler = () => {
        if (warehouseTransferUrl) {
            history.push(warehouseTransferUrl);
        }
    };

    if (!parseBoolean(displayWarehouseTransfer)) {
        return null;
    }

    return (
        <>
            {canCheckoutWithCart && (
                <StyledWrapper {...styles.wrapper}>
                    <Link {...styles.link} disabled={isLoading || isCartEmpty} onClick={goToHandler}>
                        {translate("Transfer Items")}
                    </Link>
                </StyledWrapper>
            )}
        </>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(withHistory(TransferItems)),
    definition: {
        group: "Xngage Custom" as any,
        displayName: "Transfer Items",
        allowedContexts: ["CartPage"],
    },
};

export default widgetModule;
